/**
 * Practising the questions a report named.
 *
 * A report that lists what went wrong and offers nothing to do about it is a
 * verdict. The list is the useful half — "these eight" — and the useful next step
 * is not reading the hundred-page book again, it is being asked those eight
 * questions once more and seeing what changed.
 *
 * Two ideas shape this:
 *
 *  - **The practice is the book, cut down.** Not a new player, not a new widget:
 *    the same pages holding only the questions worth another look, in book order,
 *    with the theme, the page furniture and the widgets untouched. So the practice
 *    reads like the book because it *is* the book.
 *  - **The first score is never overwritten.** The comparison is between two
 *    readings of the same questions — the one the report is about, and the one just
 *    sat — and the first is read from the report's own entries rather than
 *    recomputed. A practice that folded its answers back into the read would erase
 *    the number it is being compared against, which is the one thing this screen
 *    exists to show.
 *
 * Everything here is pure: the answers are the player's state and the entries are
 * the report's, and nothing reaches for storage or a device.
 */
import {getSlideMaxPoints} from '#/lib/scoring'
import {
  type FlashBookModel,
  type FlashBookPage,
  type FlashBookPageResult,
} from '#/types/flashbook'

/** The least a practice needs to know about a question: which block it is. */
export type PracticeTarget = {blockId: string}

/**
 * One question being practised, with the attempt it is being measured against.
 *
 * The report's own `FocusEntry` satisfies this: the first practice is compared
 * against the read, and every practice after that is compared against the practice
 * before it — so the shape is "a question and how it went last time", which both
 * of those are.
 */
export type PracticeEntry = {
  blockId: string
  label: string
  prompt: string
  correctAnswer: string
  explanation: string | null
  earnedPoints: number
  maxPoints: number
  /** Right in part — a partial score rather than a blank. */
  partial: boolean
  /** Never answered: the learner moved on. */
  skipped: boolean
}

/**
 * The book reduced to the questions being practised.
 *
 * Pages with nothing to practise are dropped rather than emptied: a practice sheet
 * that keeps blank pages would put five empty turns in front of eight questions.
 * What is kept is the *page* — its title, its order, its own direction — because a
 * question asked out of the page it lives on is a question out of context, and the
 * running head is part of what a learner is reading.
 */
export function practiceBook(
  book: FlashBookModel,
  targets: readonly PracticeTarget[],
): FlashBookModel {
  const wanted = new Set(targets.map(t => t.blockId))
  return {
    ...book,
    pages: book.pages
      .map(page => ({
        ...page,
        blocks: page.blocks.filter(block => wanted.has(block.id)),
      }))
      .filter(page => page.blocks.length > 0),
  }
}

/** How a question came out, before and after. */
export type PracticeVerdict = 'right' | 'partial' | 'wrong' | 'skipped'

export type PracticeQuestion = {
  blockId: string
  label: string
  prompt: string
  /** What the report said about the first attempt. */
  before: {points: number; maxPoints: number; verdict: PracticeVerdict}
  /**
   * What the practice did — or `null` when the practice never reached it.
   *
   * `null` is not the same as `skipped`: a question on a page the learner did not
   * turn to was never asked, and calling that "skipped again" would invent an
   * answer to a question nobody put.
   */
  after: {points: number; maxPoints: number; verdict: PracticeVerdict} | null
  /** The answer to show on a question that is still not right. */
  correctAnswer: string
  explanation: string | null
}

/** The two readings of one set of questions, and what moved between them. */
export type PracticeComparison = {
  questions: PracticeQuestion[]
  /** The first attempt, as the report measured it. */
  before: {points: number; maxPoints: number; pct: number; right: number}
  /** The practice, over the same questions. */
  after: {points: number; maxPoints: number; pct: number; right: number}
  /** Points gained — or lost — on the same questions. */
  delta: number
  /** Questions that came out right this time and were not right before. */
  fixed: PracticeQuestion[]
  /** Questions that were not right before and still are not. */
  remaining: PracticeQuestion[]
}

/**
 * Right, partly right, wrong, or never reached.
 *
 * The same division the report makes, and deliberately the same: *right* is the
 * widget's own verdict, and anything that scored something without being right is
 * partial. Comparing a partial against the report's "worth another look" and
 * getting a different word for the same attempt would make the two screens
 * disagree about one answer.
 */
function verdictOf(
  answered: boolean,
  isCorrect: boolean,
  points: number,
): PracticeVerdict {
  if (!answered) return 'skipped'
  if (isCorrect) return 'right'
  return points > 0 ? 'partial' : 'wrong'
}

/**
 * The before-and-after of one practice.
 *
 * The *before* half is read from the report's entries, which is what makes the
 * comparison trustworthy: it is the report's own verdict, not a second guess at it.
 * The *after* half is read from the practice's results by block id, so a page
 * holding two of the questions cannot have its answers crossed over.
 */
export function comparePractice({
  entries,
  pages,
  results,
}: {
  entries: readonly PracticeEntry[]
  /** The pages the practice was sat on, for the points each question is worth. */
  pages: readonly FlashBookPage[]
  results: readonly FlashBookPageResult[]
}): PracticeComparison {
  const byBlock = new Map<string, {points: number; isCorrect?: boolean}>()
  for (const page of results) {
    for (const block of page.blocks) {
      byBlock.set(block.blockId, {
        points: block.points,
        isCorrect: block.isCorrect,
      })
    }
  }
  const blockMax = new Map<string, number>()
  for (const page of pages) {
    for (const block of page.blocks) {
      blockMax.set(block.id, maxPointsOf(block))
    }
  }

  let beforePoints = 0
  let beforeMax = 0
  let beforeRight = 0
  let afterPoints = 0
  let afterMax = 0
  let afterRight = 0

  const questions: PracticeQuestion[] = entries.map(entry => {
    const beforeVerdict = verdictOf(!entry.skipped, false, entry.earnedPoints)
    const result = byBlock.get(entry.blockId)
    /*
     * A question the practice was never asked is judged at its own worth rather
     * than at zero: the denominator has to be the same on both sides or the two
     * percentages are not comparable, which is the whole point of the screen.
     */
    const max = result
      ? (blockMax.get(entry.blockId) ?? entry.maxPoints)
      : entry.maxPoints
    const after = result
      ? {
          points: result.points,
          maxPoints: max,
          verdict: verdictOf(
            result.isCorrect !== undefined,
            result.isCorrect === true,
            result.points,
          ),
        }
      : null

    beforePoints += entry.earnedPoints
    beforeMax += entry.maxPoints
    if (beforeVerdict === 'right') beforeRight += 1
    afterPoints += after?.points ?? entry.earnedPoints
    afterMax += max
    if (after?.verdict === 'right') afterRight += 1

    return {
      blockId: entry.blockId,
      label: entry.label,
      prompt: entry.prompt,
      before: {
        points: entry.earnedPoints,
        maxPoints: entry.maxPoints,
        verdict: beforeVerdict,
      },
      after,
      correctAnswer: entry.correctAnswer,
      explanation: entry.explanation,
    }
  })

  const fixed = questions.filter(q => q.after?.verdict === 'right')
  const remaining = questions.filter(q => q.after?.verdict !== 'right')

  return {
    questions,
    before: {
      points: beforePoints,
      maxPoints: beforeMax,
      pct: pctOf(beforePoints, beforeMax),
      right: beforeRight,
    },
    after: {
      points: afterPoints,
      maxPoints: afterMax,
      pct: pctOf(afterPoints, afterMax),
      right: afterRight,
    },
    delta: afterPoints - beforePoints,
    fixed,
    remaining,
  }
}

/**
 * A question as the next practice's baseline.
 *
 * A practice that is sat again is compared against *the practice*, not against the
 * read: the learner's question is "did that help?", and for the second round the
 * thing that just happened is the answer. A question the practice never reached is
 * recorded at what it was worth rather than at nothing, so the denominators stay
 * the same on both sides of the next comparison.
 */
export function questionAsEntry(question: PracticeQuestion): PracticeEntry {
  const after = question.after
  return {
    blockId: question.blockId,
    label: question.label,
    prompt: question.prompt,
    correctAnswer: question.correctAnswer,
    explanation: question.explanation,
    earnedPoints: after?.points ?? 0,
    maxPoints: after?.maxPoints ?? question.before.maxPoints,
    partial: after?.verdict === 'partial',
    skipped: after === null || after.verdict === 'skipped',
  }
}

/**
 * What a question is worth, asked of the same shared scoring the report uses.
 *
 * A practice holds only exercises by construction, so this is the block's own
 * value — never the `1` that `getSlideMaxPoints` answers for a type it does not
 * know, because a block that is not an exercise cannot be a practice question.
 */
function maxPointsOf(block: FlashBookPage['blocks'][number]): number {
  return getSlideMaxPoints(block)
}

/**
 * A percentage, with `100` for a set that is worth no points.
 *
 * The report reads the same way: a page or a set with nothing to earn has nothing
 * to lose, so a learner who has nothing to do is not shown 0%.
 */
function pctOf(points: number, maxPoints: number): number {
  if (maxPoints <= 0) return 100
  return Math.min(100, Math.max(0, Math.round((points / maxPoints) * 100)))
}
